import { Availability } from '../domain/Availability'
import { BookingDetails, BookingType } from '../domain/BookingDetails'
import { bookingPaymentTerminologyForVenueId } from '../domain/BookingPaymentTerminology'
import { BookingRequest } from '../domain/BookingRequest'
import { BookingRequestFactory } from '../domain/BookingRequestFactory'
import { BookingSubmissionSession } from '../services/booking-submission-session.service'
import { BookingService } from '../services/booking.service'
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    SimpleChanges,
    numberAttribute,
} from '@angular/core'
import { Context } from '../domain/Context'
import { ContextService } from '../services/ContextService'
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators,
} from '@angular/forms'
import { FormStep } from '../booking-form/booking-form.component'
import { HttpErrorResponse } from '@angular/common/http'
import { NGBDateTimeAdapter } from '../utilities/NgbDateTimeAdapter'
import { NgbActiveModal, NgbDateNativeAdapter, NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap'
import { Question } from '../domain/Question'
import { RxwebValidators } from '@rxweb/reactive-form-validators'
import { Subject, Subscription, finalize, takeUntil } from 'rxjs'
import { Venue } from '../domain/Venue'
import { WaitlistService } from '../services/waitlist.service'
import { adaptStringToDate } from '../utilities/helper'
import { environment } from '../../environments/environment'
import { BookingAction, LoggingService } from '../services/logging.service'
import { BookingLaunchContext, BookingModalPresentationMode } from '../landing-page/modal/landing-page-launch.types'
import { v4 as uuidv4 } from 'uuid'

@Component({
    selector: 'app-booking-modal',
    templateUrl: './booking-modal.component.html',
    providers: [BookingSubmissionSession],
})
export class BookingModalComponent implements OnInit, OnDestroy, OnChanges {

    @Input() organisationId!: string
    @Input() businessId!: string
    @Input() venueId!: string
    @Input({ transform: numberAttribute }) partySize?: number
    @Input({ transform: adaptStringToDate }) date?: Date
    @Input({ transform: adaptStringToDate }) idealTime?: Date
    @Input({
        transform: (value?: string) => value?.split(','),
    }) restrictedReasonIds?: string[]
    @Input({ alias: 'eventId' }) preselectedEventId?: string
    @Input({ alias: 'areaId' }) preselectedAreaId?: string
    @Input({ alias: 'utm_source' }) utmSource?: string
    @Input({ alias: 'utm_medium' }) utmMedium?: string
    @Input({ alias: 'utm_campaign' }) utmCampaign?: string
    @Input() hideCloseButton = false
    @Input() embedded = false
    @Input() presentationMode = BookingModalPresentationMode.LegacyIframe
    @Input() waitlistEntryId?: string
    @Output() dismissed = new EventEmitter<void>()
    @Output() bookingConfirmed = new EventEmitter<{ bookingId: string; reasonName: string | null }>()
    form!: FormGroup
    step!: FormStep
    venue?: Venue
    confirmationBookingDetails?: BookingDetails
    formStep = FormStep
    isLoading = false
    requestBookingError?: string
    bookingSlotUnavailableWarning?: string
    customerDepositAmount: number | null = null
    customerDepositIsPerCover = true
    marketingUrl = environment.marketingUrl
    private ngbDateAdapter = new NgbDateNativeAdapter()
    private ngbTimeAdapter = new NGBDateTimeAdapter()
    private onDestroy$ = new Subject<void>()
    private confirmationSubscription?: Subscription

    get paymentTerminology() {
        return bookingPaymentTerminologyForVenueId(this.venue?.id ?? this.venueId)
    }

    constructor(
        private bookingSubmissionSession: BookingSubmissionSession,
        private bookingService: BookingService,
        private contextService: ContextService,
        private fb: FormBuilder,
        private loggingService: LoggingService,
        private waitlistService: WaitlistService,
        @Optional() private activeModal: NgbActiveModal | null,
    ) {
        this.form = this.makeForm()
    }

    ngOnInit() {
        this.setBookingContextIfProvided()
        this.prefillFromWaitlistEntry()
        this.bindAvailabilityToVenue()
        this.step = FormStep.BookingSlot
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.form) {
            return
        }
        if (changes['date'] || changes['idealTime']) {
            this.setBookingContextIfProvided()
        }
    }

    configureForNgbModal(context: BookingLaunchContext) {
        this.presentationMode = BookingModalPresentationMode.NgbModal
        this.organisationId = context.organisationId
        this.businessId = context.businessId
        this.venueId = context.venueId
        this.partySize = context.partySize
        this.date = context.date
        this.idealTime = context.idealTime
        this.restrictedReasonIds = context.restrictedReasonIds
        this.preselectedEventId = context.eventId
        this.preselectedAreaId = context.areaId
        this.utmSource = context.utmSource
        this.utmMedium = context.utmMedium
        this.utmCampaign = context.utmCampaign
        this.setBookingContextIfProvided()
    }

    private setBookingContextIfProvided() {
        if (this.shouldIgnorePreselectedReasons()) {
            this.restrictedReasonIds = undefined
        }
        const bookingSlotForm = this.form.get(FormStep[FormStep.BookingSlot]) as FormGroup
        if (this.partySize) {
            bookingSlotForm
                .get('partySize')!
                .setValue(this.partySize)
        }
        if (this.date) {
            bookingSlotForm
                .get('date')!
                .setValue(this.ngbDateAdapter.fromModel(this.date))
        }
        if (this.idealTime) {
            bookingSlotForm
                .get('idealTime')!
                .setValue(this.idealTime)
        }
    }

    private shouldIgnorePreselectedReasons() {
        const ccBloomsGoogleReasonId = 'fce77600-14a6-4e8b-9f6a-a1fd41308ece'
        const isCcBloomsGoogleReason = this.restrictedReasonIds?.length === 1
            && this.restrictedReasonIds[0] === ccBloomsGoogleReasonId
        if (isCcBloomsGoogleReason) {
            return true
        }
        return false
    }

    private prefillFromWaitlistEntry() {
        if (!this.waitlistEntryId) {
            return
        }
        this.waitlistService.getWaitlistEntry(this.waitlistEntryId)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
                next: entry => {
                    const bookingSlotForm = this.form.get(FormStep[FormStep.BookingSlot]) as FormGroup
                    const bookingDetailsForm = this.form.get(FormStep[FormStep.BookingDetails]) as FormGroup

                    bookingSlotForm.get('partySize')!.setValue(entry.partySize)

                    if (entry.date) {
                        const [year, month, day] = entry.date.split('-').map(Number)
                        const entryDate = new Date(year, month - 1, day)
                        bookingSlotForm.get('date')!.setValue(this.ngbDateAdapter.fromModel(entryDate))
                    }

                    if (entry.time) {
                        const [hours, minutes] = entry.time.split(':').map(Number)
                        const entryTime = new Date()
                        entryTime.setHours(hours, minutes, 0, 0)
                        bookingSlotForm.get('idealTime')!.setValue(entryTime)
                    }

                    if (entry.reasonId) {
                        bookingSlotForm.get('selectedReasonId')!.setValue(entry.reasonId)
                    }
                    if (entry.eventId) {
                        bookingSlotForm.get('selectedEventId')!.setValue(entry.eventId)
                    }
                    if (entry.requiresWheelchairAccess) {
                        bookingSlotForm.get('requiresWheelchairAccess')!.setValue(true)
                    }
                    if (entry.requiresDogFriendly) {
                        bookingSlotForm.get('requiresDogFriendly')!.setValue(true)
                    }
                    if (entry.requiresPramAccessible) {
                        bookingSlotForm.get('requiresPramAccessible')!.setValue(true)
                    }
                    if (entry.requiresHighchairFriendly) {
                        bookingSlotForm.get('requiresHighchairFriendly')!.setValue(true)
                    }

                    bookingDetailsForm.get('firstName')!.setValue(entry.firstName)
                    bookingDetailsForm.get('lastName')!.setValue(entry.lastName)
                    bookingDetailsForm.get('emailAddress')!.setValue(entry.emailAddress)
                },
            })
    }

    ngOnDestroy() {
        this.onDestroy$.next()
        this.onDestroy$.complete()
    }

    bookingOptionsConfirmed(_: FormGroup, availability: Availability) {
        this.setWheelchairAccessRequiredValidator(availability)
        this.setDogFriendlyRequiredValidator(availability)
        this.setPramAccessibleRequiredValidator(availability)
        this.setHighchairFriendlyRequiredValidator(availability)
        this.setPaymentDetailsRequired(availability)
        this.setDepositRequired(availability)
        this.setPhoneNumberRequired(availability.venue)
        this.setReasonRequired(availability)
        this.setSelectedReasonQuestions(availability.venue)
        this.setAgreedToBookingPolicyRequired(availability.venue)
        this.goToStepAfterConfirmingBookingOptions()
    }

    clearRequestBookingError() {
        this.requestBookingError = undefined
        this.bookingSlotUnavailableWarning = undefined
    }

    private resetCustomerDepositState() {
        this.customerDepositAmount = null
        this.customerDepositIsPerCover = true
    }

    goBackFromBookingPolicy() {
        const bookingPolicyGroup = this.form.get(FormStep[FormStep.BookingPolicy]) as FormGroup
        bookingPolicyGroup.get('agreedToBookingPolicy')?.setValue(null)
        this.step = FormStep.BookingSlot
    }

    goNextFromBookingPolicy() {
        const bookingPolicyGroup = this.form.get(FormStep[FormStep.BookingPolicy]) as FormGroup
        bookingPolicyGroup.get('agreedToBookingPolicy')?.setValue(true)
        this.goToStepAfterConfirmingBookingOptions()
    }

    goBackFromBookingDetails() {
        const bookingPolicyGroup = this.form.get(FormStep[FormStep.BookingPolicy]) as FormGroup
        const hasAgreedToBookingPolicy = bookingPolicyGroup.get('agreedToBookingPolicy')?.value
        if (hasAgreedToBookingPolicy) {
            this.step = FormStep.BookingPolicy
        } else {
            this.step = FormStep.BookingSlot
        }
    }

    goNextFromBookingDetails() {
        const paymentDetailsRequiredControl = this.form
            .get(FormStep[FormStep.BookingDetails])
            ?.get('paymentDetailsRequired') as FormControl
        const depositRequiredControl = this.form
            .get(FormStep[FormStep.BookingDetails])
            ?.get('depositRequired') as FormControl
        if (paymentDetailsRequiredControl.value) {
            this.loggingService.trackAction(BookingAction.ProceedToCardDetails)
            this.step = FormStep.PaymentDetails
        } else if (depositRequiredControl.value) {
            this.resetCustomerDepositState()
            this.loggingService.trackAction(BookingAction.ProceedToDepositPayment)
            this.step = FormStep.Deposit
        }
    }

    goBackFromPaymentDetails() {
        this.requestBookingError = undefined
        this.step = FormStep.BookingDetails
    }

    goBackFromDeposit() {
        this.requestBookingError = undefined
        this.resetCustomerDepositState()
        this.step = FormStep.BookingDetails
    }

    goBackFromWaitingForConfirmation() {
        this.confirmationSubscription?.unsubscribe()
        this.requestBookingError = undefined
        const waitingGroup = this.form.get(FormStep[FormStep.WaitingForConfirmation]) as FormGroup
        waitingGroup.get('isWaiting')?.setValue(false)
        const depositPaymentIntentId = this.form.get(FormStep[FormStep.Deposit])?.get('paymentIntentId')?.value
        const setupIntentId = this.form.get(FormStep[FormStep.PaymentDetails])?.get('setupIntentId')?.value
        if (depositPaymentIntentId) {
            this.form.get(FormStep[FormStep.Deposit])?.get('paymentIntentId')?.setValue(null)
            this.step = FormStep.Deposit
        } else if (setupIntentId) {
            this.form.get(FormStep[FormStep.PaymentDetails])?.get('setupIntentId')?.setValue(null)
            this.step = FormStep.PaymentDetails
        } else {
            this.step = FormStep.PaymentDetails
        }
    }

    cardDetailsSkipped() {
        this.step = FormStep.BookingDetails
        this.bookSelected()
    }

    bookSelected() {
        this.loggingService.trackAction(BookingAction.Book)
        return this.submitCurrentBookingRequest()
    }

    private submitCurrentBookingRequest() {
        const bookingSlotForm = this.form.get(FormStep[FormStep.BookingSlot]) as FormGroup
        const bookingRequestIdControl = bookingSlotForm.get('bookingRequestId')!
        if (!bookingRequestIdControl.value) {
            bookingRequestIdControl.setValue(uuidv4())
        }
        const factory = new BookingRequestFactory(
            this.form, 
            this.utmSource || null,
            this.utmMedium || null,
            this.utmCampaign || null
        )
        const request = factory.make()
        return this.submitBookingRequest(request)
    }

    private submitBookingRequest(request: BookingRequest) {
        this.resetCustomerDepositState()
        this.requestBookingError = undefined
        this.bookingSlotUnavailableWarning = undefined
        this.isLoading = true
        return this.bookingSubmissionSession.submit(
            new Context(this.organisationId, this.businessId, this.venueId),
            request
        )
            .pipe(
                takeUntil(this.onDestroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: bookingDetails => {
                    this.resetCustomerDepositState()
                    this.confirmationBookingDetails = bookingDetails
                    this.loggingService.trackAction(BookingAction.ViewBookingConfirmation)
                    this.step = FormStep.Confirmation
                    this.postBookingConfirmedMessage(bookingDetails)
                },
                error: (error: HttpErrorResponse) => {
                    if (this.bookingSubmissionSession.isDefinitiveFailure(error)) {
                        this.form
                            .get(FormStep[FormStep.BookingSlot])
                            ?.get('bookingRequestId')
                            ?.setValue(null)
                    }
                    if (error.status === 402 && error.error?.depositAmount != null) {
                        this.customerDepositAmount = error.error.depositAmount
                        this.customerDepositIsPerCover = error.error.depositAmountIsPerCover ?? true
                        this.step = FormStep.Deposit
                    } else {
                        this.resetCustomerDepositState()
                        if (error.status === 404) {
                            this.bookingSlotUnavailableWarning = this.mapUnavailableSlotError(error)
                        } else if (error.status === 400 && error.error?.message?.includes('EMAIL_VALIDATION_FAILED')) {
                            const emailControl = this.form
                                .get(FormStep[FormStep.BookingDetails])
                                ?.get('emailAddress')
                            emailControl?.setErrors({ serverValidation: true })
                            emailControl?.markAsTouched()
                        } else {
                            this.requestBookingError = this.mapErrorResponseToError(error)
                        }
                    }
                },
            })
    }
    
    bookWaitForResponseSelected() {
        this.confirmationSubscription?.unsubscribe()
        const waitingGroup = this.form.get(FormStep[FormStep.WaitingForConfirmation]) as FormGroup
        waitingGroup.get('isWaiting')?.setValue(true)
        this.step = FormStep.WaitingForConfirmation
        this.clearRequestBookingError()
        const setupIntentId = this.form.get('PaymentDetails.setupIntentId')?.value ?? undefined
        const paymentIntentId = this.form.get('Deposit.paymentIntentId')?.value ?? undefined
        if (!setupIntentId && !paymentIntentId) {
            this.onWaitingErrorOccurred('Missing intent ID. Please try again.')
            return
        }
        this.confirmationSubscription = this.bookingService.waitForPaymentConfirmation(
            new Context(this.organisationId, this.businessId, this.venueId),
            { setupIntentId, paymentIntentId }
        ).pipe(takeUntil(this.onDestroy$)).subscribe({
            next: details => this.onWaitingConfirmationReceived(details),
            error: error => {
                this.loggingService.noticeError(error)
                this.onWaitingErrorOccurred('Error checking booking status. Please check your email inbox for confirmation.')
            },
        })
    }

    onWaitingConfirmationReceived(bookingDetails: BookingDetails) {
        this.confirmationBookingDetails = bookingDetails
        this.loggingService.trackAction(BookingAction.ViewBookingConfirmation)
        this.step = FormStep.Confirmation
        this.postBookingConfirmedMessage(bookingDetails)
    }

    onWaitingErrorOccurred(error: string) {
        this.requestBookingError = error
        // Stay on the waiting step so user can see the error
    }

    bookAgainSelected() {
        this.resetCustomerDepositState()
        this.bookingSubmissionSession.reset()
        this.confirmationBookingDetails = undefined
        this.form = this.makeForm()
        this.step = FormStep.BookingSlot
    }

    dismiss() {
        if (this.embedded) {
            return
        }
        this.dismissed.emit()
        if (this.presentationMode === BookingModalPresentationMode.NgbModal) {
            this.activeModal?.dismiss()
            return
        }
        window.parent.postMessage(
            {
                type: 'close-booking-modal',
                payload: {
                },
            },
            '*'
        )
    }

    private postBookingConfirmedMessage(bookingDetails: BookingDetails) {
        if (this.embedded) {
            return
        }
        const payload = {
            bookingId: bookingDetails.bookingId,
            reasonName: bookingDetails.reasonName ?? null,
        }
        if (this.presentationMode === BookingModalPresentationMode.NgbModal) {
            this.bookingConfirmed.emit(payload)
            return
        }
        window.parent.postMessage(
            {
                type: 'booking-confirmed',
                payload,
            },
            '*'
        )
    }

    get suppressIframeModalChrome(): boolean {
        return this.embedded || this.presentationMode === BookingModalPresentationMode.NgbModal
    }

    private makeForm() {
        return this.fb.group({
            'BookingSlot': this.fb.group({
                availability: [null, Validators.required],
                step: [null, Validators.required],
                partySize: [null, Validators.required],
                idealTime: [null, Validators.required],
                date: [null, Validators.required],
                requiresWheelchairAccess: [null],
                requiresDogFriendly: [null],
                requiresPramAccessible: [null],
                requiresHighchairFriendly: [null],
                selectedAreaId: [null, Validators.required],
                areaSelectionIsPossible: [true, Validators.required],
                bookingType: [null, Validators.required],
                selectedEventId: [null],
                selectedReasonId: [null],
                time: [null, Validators.required],
                endTime: [null, Validators.required],
                bookingRequestId: [null, null],
            }),
            'BookingPolicy': this.fb.group({
                agreedToBookingPolicy: [null, []],
            }),
            'BookingDetails': this.fb.group({
                paymentDetailsRequired: [false, Validators.required],
                depositRequired: [false, Validators.required],
                firstName: [null, Validators.required],
                lastName: [null, Validators.required],
                emailAddress: [null, [Validators.required, RxwebValidators.email()]],
                phoneNumber: [null],
                notes: [null],
                questions: new FormArray([]),
                agreedToMarketing: [true, Validators.required],
                agreedToTerms: [false, Validators.requiredTrue],
            }),
            'Order': this.fb.group({
                lineItems: new FormArray([]),
            }),
            'PaymentDetails': this.fb.group({
                setupIntentId: [null, null],
            }),
            'Deposit': this.fb.group({
                paymentIntentId: [null, null],
            }),
            'WaitingForConfirmation': this.fb.group({
                isWaiting: [false, Validators.required],
            }),
        })
    }

    private setWheelchairAccessRequiredValidator(availability: Availability) {
        const wheelchairAccessControl = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('requiresWheelchairAccess') as FormControl
        const needsToBeSet = availability.venue.wheelchairAccessibleQuestionEnabled
        if (needsToBeSet) {
            wheelchairAccessControl.setValidators([Validators.required])
        } else {
            wheelchairAccessControl.clearValidators()
            wheelchairAccessControl.setValue(null)
        }
    }

    private setDogFriendlyRequiredValidator(availability: Availability) {
        const dogFriendlyControl = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('requiresDogFriendly') as FormControl
        const needsToBeSet = availability.venue.dogFriendlyQuestionEnabled
        if (needsToBeSet) {
            dogFriendlyControl.setValidators([Validators.required])
        } else {
            dogFriendlyControl.clearValidators()
            dogFriendlyControl.setValue(null)
        }
    }

    private setPramAccessibleRequiredValidator(availability: Availability) {
        const pramAccessibleControl = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('requiresPramAccessible') as FormControl
        const needsToBeSet = availability.venue.pramAccessibleQuestionEnabled
        if (needsToBeSet) {
            pramAccessibleControl.setValidators([Validators.required])
        } else {
            pramAccessibleControl.clearValidators()
            pramAccessibleControl.setValue(null)
        }
    }

    private setHighchairFriendlyRequiredValidator(availability: Availability) {
        const highchairFriendlyControl = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('requiresHighchairFriendly') as FormControl
        const needsToBeSet = availability.venue.highchairFriendlyQuestionEnabled
        if (needsToBeSet) {
            highchairFriendlyControl.setValidators([Validators.required])
        } else {
            highchairFriendlyControl.clearValidators()
            highchairFriendlyControl.setValue(null)
        }
    }

    private setPaymentDetailsRequired(availability: Availability) {
        const areaId = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedAreaId')?.value as string
        const partySize = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('partySize')?.value as number
        const reasonId = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedReasonId')?.value as string | null
        const eventId = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedEventId')?.value as string | null
        const ngbTime = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('time')?.value as NgbTimeStruct
        const ngbDate = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('date')?.value as NgbDateStruct
        const date = this.ngbDateAdapter.toModel(ngbDate)!
        const time = this.ngbTimeAdapter.toModel(ngbTime)!
        const dateTime = availability.getDateTimeForBookingSlotDateAndTime(
            date,
            time
        )
        const required = availability.reservableOptionWithConfiguration(
            areaId,
            partySize,
            reasonId,
            eventId,
            dateTime
        )?.cancellationChargeAmount !== null
        const paymentDetailsRequiredControl = this.form
            .get(FormStep[FormStep.BookingDetails])
            ?.get('paymentDetailsRequired') as FormControl
        paymentDetailsRequiredControl.setValue(required)
    }

    private setDepositRequired(availability: Availability) {
        const areaId = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedAreaId')?.value as string
        const partySize = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('partySize')?.value as number
        const reasonId = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedReasonId')?.value as string | null
        const eventId = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedEventId')?.value as string | null
        const ngbTime = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('time')?.value as NgbTimeStruct
        const ngbDate = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('date')?.value as NgbDateStruct
        const date = this.ngbDateAdapter.toModel(ngbDate)!
        const time = this.ngbTimeAdapter.toModel(ngbTime)!
        const dateTime = availability.getDateTimeForBookingSlotDateAndTime(
            date,
            time
        )
        const required = availability.reservableOptionWithConfiguration(
            areaId,
            partySize,
            reasonId,
            eventId,
            dateTime
        )?.depositAmount !== null
        const depositRequiredControl = this.form
            .get(FormStep[FormStep.BookingDetails])
            ?.get('depositRequired') as FormControl
        depositRequiredControl.setValue(required)
    }

    private setPhoneNumberRequired(venue: Venue) {
        const phoneNumberControl = this.form
            .get(FormStep[FormStep.BookingDetails])
            ?.get('phoneNumber') as FormControl
        if (venue.phoneNumberRequired) {
            phoneNumberControl.setValidators([Validators.required])
        } else {
            phoneNumberControl.setValidators([])
        }
    }

    private setReasonRequired(availability: Availability) {
        const reasonControl = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedReasonId') as FormControl
        if (this.isReasonRequired(availability)) {
            reasonControl.setValidators([Validators.required])
        } else {
            reasonControl.clearValidators()
        }
    }

    private isReasonRequired(availability: Availability): boolean {
        const form = this.form.get(FormStep[FormStep.BookingSlot]) as FormGroup
        const venueHasReasons = availability.isConfiguredForAnyReason()
        const bookingType = form.get('bookingType')?.value
        if (bookingType === BookingType.Event) {
            return false
        }
        return venueHasReasons
    }

    private setSelectedReasonQuestions(venue: Venue) {
        const questionControls = this.form
            .get(FormStep[FormStep.BookingDetails])
            ?.get('questions') as FormArray
        const reasonControl = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedReasonId') as FormControl
        const eventControl = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('selectedEventId') as FormControl
        const questions = venue.questionsToAskWhenBookingForConfiguration(
            reasonControl.value,
            eventControl.value
        )
        questionControls.clear()
        questions.forEach(question => {
            let validators = this.validatorsForQuestion(question)
            questionControls.push(this.fb.group({
                question: new FormControl(question, Validators.required),
                answer: new FormControl(null, validators),
            }))
        })
    }

    private validatorsForQuestion(question: Question) {
        const isRequired = question.required && !question.isTickable
        const isRequiredTrue = question.required && question.isTickable
        let validators: ValidatorFn[] = []
        if (isRequired) {
            validators.push(Validators.required)
        }
        if (isRequiredTrue) {
            validators.push(Validators.requiredTrue)
        }
        return validators
    }

    private setAgreedToBookingPolicyRequired(venue: Venue) {
        const agreedToBookingPolicyControl = this.form
            .get(FormStep[FormStep.BookingPolicy])
            ?.get('agreedToBookingPolicy') as FormControl
        const partySize = this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('partySize')?.value as number

        // Check if any policy needs to be shown based on party size
        const hasBasePolicy = venue.bookingPolicy?.length && venue.bookingPolicy.length > 0
        const hasLargePartyPolicy = venue.largePartyBookingPolicy?.length && venue.largePartyBookingPolicy.length > 0
        const meetsLargePartyThreshold = venue.largePartyBookingPolicyMinimumPartySize === null ||
                                        partySize >= venue.largePartyBookingPolicyMinimumPartySize

        // Show policies based on party size and availability
        const shouldShowAnyPolicy = hasBasePolicy ||
                                    (hasLargePartyPolicy && meetsLargePartyThreshold)
        if (shouldShowAnyPolicy) {
            agreedToBookingPolicyControl.setValidators([Validators.requiredTrue])
        } else {
            agreedToBookingPolicyControl.clearValidators()
        }
        agreedToBookingPolicyControl.updateValueAndValidity()
    }

    private goToStepAfterConfirmingBookingOptions() {
        const bookingPolicyGroup = this.form.get(FormStep[FormStep.BookingPolicy]) as FormGroup
        const policyNeedsToBeAgreedTo = !bookingPolicyGroup.valid
        if (policyNeedsToBeAgreedTo) {
            this.step = FormStep.BookingPolicy
        } else {
            this.step = FormStep.BookingDetails
        }
    }

    private mapUnavailableSlotError(error: HttpErrorResponse) {
        return 'Sorry, this booking slot is no longer available. Please select another time.';
    }

    private mapErrorResponseToError(error: HttpErrorResponse) {
        if (error.status === 0) {
            return 'There was a connection problem. Please try again.'
        }
        return 'There was a problem requesting your booking. Please try again.'
    }

    private bindAvailabilityToVenue() {
        this.form
            .get(FormStep[FormStep.BookingSlot])
            ?.get('availability')
            ?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(availability => {
                this.venue = availability.venue
            })
    }
}
